import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { type SelectableBank } from '../api/use-selectable-banks';

// The issuing-bank picker for the review dialog: the (entity, currency)
// catalog dropdown plus the read-only detail lines of the selected bank.

// The bank catalog is (entity, currency)-filtered, so an empty list with a
// resolved currency means the catalog has no row for it yet — a different fix
// than an unresolved currency.
function bankPlaceholder(isLoading: boolean, count: number, currency: string | null): string {
  if (isLoading) return 'Loading banks…';
  if (count === 0) {
    return currency === null
      ? 'No banks available — resolve the invoice currency first'
      : `No ${currency} bank in the issuing entity's catalog — add one to issue in ${currency}`;
  }
  return 'Select a bank…';
}

export function ReadOnlyRow({ label, value }: Readonly<{ label: string; value: string | null }>) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value ?? '—'}</span>
    </div>
  );
}

function BankDetails({ bank }: Readonly<{ bank: SelectableBank }>) {
  return (
    <div className="mt-2 space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
      <ReadOnlyRow label="Beneficiary" value={bank.bank_beneficiary} />
      <ReadOnlyRow label="Bank" value={bank.bank_name} />
      <ReadOnlyRow label="IBAN / Account" value={bank.iban_or_account} />
      <ReadOnlyRow label="SWIFT" value={bank.swift} />
      {bank.intermediary_bank !== null ? (
        <ReadOnlyRow label="Intermediary" value={bank.intermediary_bank} />
      ) : null}
    </div>
  );
}

export interface BankFieldProps {
  readonly banks: SelectableBank[];
  readonly isLoading: boolean;
  readonly bankId: string;
  readonly currency: string | null;
  readonly onBankChange: (id: string) => void;
}

export function BankField({
  banks,
  isLoading,
  bankId,
  currency,
  onBankChange,
}: Readonly<BankFieldProps>) {
  const selectedBank = banks.find((b) => b.id === bankId) ?? null;
  return (
    <div className="space-y-1">
      <Label htmlFor="review-bank">Issuing bank</Label>
      <Select
        id="review-bank"
        value={bankId}
        onChange={(e) => {
          onBankChange(e.target.value);
        }}
        disabled={isLoading}
      >
        <option value="" disabled>
          {bankPlaceholder(isLoading, banks.length, currency)}
        </option>
        {banks.map((bank) => (
          <option key={bank.id} value={bank.id}>
            {bank.account_type !== null ? `${bank.account_type} — ` : ''}
            {bank.bank_name} ({bank.iban_or_account})
          </option>
        ))}
      </Select>
      {selectedBank !== null ? <BankDetails bank={selectedBank} /> : null}
    </div>
  );
}
